/**
 * Comparativos (`/comparar/[slug]`). Três famílias:
 * - contra o jeito manual (WhatsApp, planilha, papel);
 * - contra um TIPO de app (cobrado por profissional, marketplace);
 * - entre dois jeitos de trabalhar (Pix direto ou link, sessão ou mensalidade).
 *
 * Nunca citar nome de outro produto ou empresa. O lado de lá fala do tipo em
 * geral ("varia"); onde nós não temos, "não tem". "Sistema" só no `title`/`h1`
 * (termo de busca, D5), nunca no corpo. Recurso pago leva `paid: true`, que a
 * tabela mostra como "(planos pagos)". Número de produto vem de `facts.ts`.
 */

import { formatBRL, FREE_PLAN, PAYMENT_FEES, PROSE, REMINDERS, SOLO_PLANS, TEAM_PLAN } from '../facts';

export interface ComparisonRow {
  criterion: string;
  us: string;
  them: string;
  /** A célula da Controlapag é recurso dos planos pagos. */
  paid?: true;
}

export interface ComparisonPage {
  slug: string;
  /** Nome do outro lado, para o cabeçalho da tabela ("Agenda no WhatsApp"). */
  other: string;
  /** O mesmo com artigo, para o meio da frase ("a agenda no WhatsApp"). */
  otherWithArticle: string;
  /** Cabeçalho da nossa coluna, quando não é só "Controlapag". */
  usLabel?: string;
  /** `manual`: contra o jeito à mão. `category`: contra um tipo de app. `practice`: dois jeitos de trabalhar. */
  kind: 'manual' | 'category' | 'practice';
  /** Título-base, até 60 caracteres (`pageTitle()` decide o sufixo). */
  title: string;
  description: string;
  h1: string;
  /** Resposta direta: quando escolher cada um. */
  answer: string;
  rows: ComparisonRow[];
  chooseUs: string[];
  chooseThem: string[];
  /** Títulos dos cartões "escolha X se", quando o padrão não serve. */
  chooseUsTitle?: string;
  chooseThemTitle?: string;
  faq: { question: string; answer: string }[];
  /** Data da primeira publicação (AAAA-MM-DD). */
  publishedAt: string;
  /** Data em que a comparação foi conferida (AAAA-MM-DD). */
  checkedAt: string;
}

const prices = SOLO_PLANS.map((plan) => plan.webPrice);
const OUR_PRICE = `Conta grátis para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês; planos de ${formatBRL(
  Math.min(...prices),
)} a ${formatBRL(Math.max(...prices))} por mês no site`;
const TEAM_PRICE = `${formatBRL(TEAM_PLAN.orgFee)} da conta + ${formatBRL(TEAM_PLAN.perProfessional)} por profissional + ${formatBRL(
  TEAM_PLAN.perClientBlock,
)} a cada ${TEAM_PLAN.clientBlockSize} clientes`;
const FEES = `${PROSE.pixFee}, ${PROSE.cardFee} ou ${PROSE.boletoFee}`;
const CTA = 'Crie sua conta grátis e conheça.';

/** Frase que fica abaixo de toda tabela: o que é grátis e o que é pago. */
export const FREE_VS_PAID = `Na conta grátis da Controlapag você tem agenda, cadastro de clientes e cobranças avulsas para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês. O que está marcado como "(planos pagos)" começa em ${formatBRL(
  Math.min(...prices),
)} por mês no site.`;

// Recebimento de R$ 100 pelo link, por forma de pagamento.
const net = (gross: number, percent: number, fixed = 0) => gross - (gross * percent) / 100 - fixed;
const NET_100 = `Pix: chegam ${formatBRL(net(100, PAYMENT_FEES.pixPercent))}; cartão: ${formatBRL(
  net(100, PAYMENT_FEES.cardPercent, PAYMENT_FEES.cardFixed),
)}; boleto: ${formatBRL(net(100, 0, PAYMENT_FEES.boletoFixed))}`;

// Exemplo da página de sessão x mensalidade (valores de exemplo, não de produto).
const SESSION = 80;
const SESSIONS_PER_MONTH = 4;
const MONTHLY = 300;

export const COMPARISON_PAGES: ComparisonPage[] = [
  {
    slug: 'agenda-no-whatsapp',
    other: 'Agenda no WhatsApp',
    otherWithArticle: 'a agenda no WhatsApp',
    kind: 'manual',
    title: 'WhatsApp ou app de agendamento?',
    description:
      'Marcar horário pelo WhatsApp ou usar um link de agendamento: tempo gasto, horário duplicado, lembrete, cobrança e quando cada um faz sentido.',
    h1: 'Agenda no WhatsApp ou sistema de agendamento?',
    answer: `Com poucos clientes por semana, o WhatsApp resolve. Quando você responde "tem horário?" várias vezes por dia, um link de agendamento com lembrete e cobrança no WhatsApp devolve esse tempo, nos planos pagos ${PROSE.fromPrice}.`,
    rows: [
      { criterion: 'Cliente marca sozinho', us: 'Sim, pelo link do perfil público, a qualquer hora', them: 'Não, depende de você responder', paid: true },
      { criterion: 'Horário duplicado', us: 'A agenda bloqueia o horário ocupado', them: 'Acontece quando a conversa se perde' },
      { criterion: 'Lembrete antes do horário', us: '1 por agendamento, automático no WhatsApp', them: 'Manual, um por um', paid: true },
      { criterion: 'Cobrança', us: `Cobrança avulsa e "Confirmar recebimento"; pelo link, com taxa (${FEES})`, them: 'Chave Pix mandada à mão; conferir o extrato' },
      { criterion: 'Mensalidade recorrente', us: 'Gerada todo mês, com lembrete de vencimento', them: 'Lembrar e cobrar todo mês', paid: true },
      { criterion: 'Saber quanto entrou', us: 'Painel com "Recebido no mês" e "A receber"', them: 'Caderno ou planilha à parte' },
      { criterion: 'Custo', us: OUR_PRICE, them: 'Grátis, mas custa o seu tempo' },
    ],
    chooseUs: [
      'Você responde mensagem de horário várias vezes por dia',
      'Tem clientes de mensalidade',
      'Falta sem aviso já te deu prejuízo',
    ],
    chooseThem: ['Atende poucas pessoas por semana', 'Todos os clientes são fixos e pagam em dia'],
    faq: [
      {
        question: 'Dá para continuar usando o WhatsApp com um app de agenda?',
        answer:
          'Sim. O WhatsApp continua sendo o canal: o link de agendamento vai na conversa ou na bio, e lembretes e cobranças chegam no WhatsApp do cliente, nos planos pagos.',
      },
      {
        question: 'O WhatsApp Business já não tem agenda?',
        answer:
          'Em setembro de 2026, o WhatsApp Business tinha catálogo e mensagens automáticas, mas não uma agenda que bloqueia horários nem cobrança recorrente.',
      },
    ],
    publishedAt: '2026-09-28',
    checkedAt: '2026-10-03',
  },
  {
    slug: 'planilha',
    other: 'Planilha',
    otherWithArticle: 'a planilha',
    kind: 'manual',
    title: 'Planilha ou app para agenda e mensalidades?',
    description:
      'Planilha de clientes e mensalidades ou app de agenda e cobrança? O que cada um faz, onde a planilha falha e quando vale trocar, com o que é grátis e pago.',
    h1: 'Planilha ou sistema para agenda e mensalidades?',
    answer:
      'Planilha registra; ela não avisa, não cobra e não bloqueia horário. Se você já esqueceu de cobrar alguém ou marcou 2 clientes no mesmo horário, a planilha deixou de ser suficiente.',
    rows: [
      { criterion: 'Registro de clientes', us: 'Sim, com histórico de atendimentos e pagamentos', them: 'Sim, se você mantiver atualizada' },
      { criterion: 'Agenda com horários livres', us: 'Sim; o cliente marca pelo link do perfil público', them: 'Não' },
      { criterion: 'Aviso de vencimento', us: `Automático no WhatsApp, ${REMINDERS.chargeDaysBeforeOptions.join(' ou ')} dias antes e no dia`, them: 'Não', paid: true },
      { criterion: 'Receber por Pix ou cartão', us: `Link de pagamento que dá baixa sozinho, com taxa (${FEES})`, them: 'Conferir extrato e marcar à mão', paid: true },
      { criterion: 'Acesso pelo celular', us: `App ${PROSE.platforms}`, them: 'Depende do app de planilha' },
      { criterion: 'Custo', us: OUR_PRICE, them: 'Grátis' },
    ],
    chooseUs: ['Tem mais de 10 clientes recorrentes', 'Esquece de cobrar ou de dar baixa', 'Quer que o cliente marque sozinho'],
    chooseThem: ['Tem poucos clientes e gosta de controlar tudo à mão'],
    faq: [
      {
        question: 'Consigo levar os clientes da planilha para a Controlapag?',
        answer:
          'Sim, mas um por um: não existe importação de planilha. O assistente da criação da conta monta os serviços e a agenda do seu ramo, e você cadastra os clientes.',
      },
    ],
    publishedAt: '2026-09-28',
    checkedAt: '2026-10-03',
  },
  {
    slug: 'sistema-cobrado-por-profissional',
    other: 'App cobrado por profissional',
    otherWithArticle: 'o app cobrado por profissional',
    kind: 'category',
    title: 'Agenda: preço por profissional ou por cliente?',
    description:
      'Como o preço do app de agenda cresce: por profissional, com comissão e estoque, ou pelo número de clientes, com mensalidade e contrato. E quando cada um vale.',
    h1: 'Sistema cobrado por profissional ou por cliente atendido?',
    answer: `Apps cobrados por profissional trazem comissão e estoque, úteis para salão com equipe que vende produto. Na Controlapag, quem atende sozinho paga pelo número de clientes; no plano Equipe, são ${TEAM_PRICE}.`,
    rows: [
      { criterion: 'Agendamento online com link', us: 'Sim', them: 'Em geral, sim', paid: true },
      { criterion: 'Lembrete no WhatsApp', us: 'Sim, 1 por agendamento', them: 'Varia', paid: true },
      { criterion: 'Pix e cartão pelo link', us: `Sim, com taxa (${FEES})`, them: 'Varia', paid: true },
      { criterion: 'Mensalidade com cota de sessões', us: 'Sim, a cota do período não acumula', them: 'Varia', paid: true },
      { criterion: 'Contrato com cobrança recorrente', us: 'Sim', them: 'Nem sempre', paid: true },
      { criterion: 'Comissões da equipe', us: 'Não tem', them: 'Comum' },
      { criterion: 'Controle de estoque', us: 'Não tem', them: 'Comum' },
      {
        criterion: 'Como o preço cresce',
        us: `Sozinho: pelo número de clientes no mês. Equipe: ${TEAM_PRICE}`,
        them: 'Pelo número de profissionais',
      },
    ],
    chooseUs: [
      'Sua receita vem de mensalidade ou plano mensal',
      'Você atende sozinho ou com equipe pequena',
      'Quer contrato e cobrança ligados à agenda',
    ],
    chooseThem: ['Precisa calcular comissão de vários profissionais', 'Vende produto e precisa de estoque'],
    faq: [
      {
        question: 'O que sai mais barato para quem atende sozinho?',
        answer: `Compare o preço para 1 profissional com o plano pelo seu número de clientes por mês. A Controlapag tem conta grátis para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês e planos ${PROSE.fromPrice}. ${CTA}`,
      },
      {
        question: 'Quanto custa a Controlapag para uma equipe?',
        answer: `No plano Equipe, ${TEAM_PRICE}, de ${TEAM_PLAN.minProfessionals} a ${TEAM_PLAN.maxProfessionals} profissionais, a partir de ${formatBRL(
          TEAM_PLAN.fromPrice,
        )} por mês. É contratado pelo site.`,
      },
    ],
    publishedAt: '2026-09-28',
    checkedAt: '2026-10-03',
  },
  {
    slug: 'marketplace-de-beleza',
    other: 'App de marketplace',
    otherWithArticle: 'o marketplace',
    kind: 'category',
    title: 'Página própria ou marketplace de agendamento?',
    description:
      'Marketplace de agendamento ou página própria com cobrança? O que cada modelo faz com seus clientes, seus preços e suas mensalidades antes de escolher.',
    h1: 'Página própria ou app de marketplace?',
    answer:
      'Marketplaces trazem clientes novos, mas você divide a vitrine com outros profissionais e o foco é o horário avulso. Na Controlapag, o perfil público é uma página sua, com lembrete no WhatsApp e mensalidade; na busca, você também aparece ao lado de outros profissionais.',
    rows: [
      { criterion: 'Para quem', us: 'Qualquer serviço com hora marcada', them: 'Em geral beleza e bem-estar' },
      { criterion: 'Página de agendamento', us: 'Perfil público só seu, com seus serviços e fotos', them: 'Perfil dentro do app, ao lado de outros', paid: true },
      { criterion: 'Aparecer para clientes novos', us: 'Busca da Controlapag, ao lado de outros profissionais da cidade', them: 'Principal vantagem do modelo', paid: true },
      { criterion: 'Lembrete ao cliente', us: 'WhatsApp, 1 por agendamento', them: 'Varia (SMS, notificação do app, e-mail)', paid: true },
      { criterion: 'Mensalidade e contrato', us: 'Sim', them: 'Nem sempre', paid: true },
      { criterion: 'Preço', us: OUR_PRICE, them: 'Varia; confira se há comissão' },
    ],
    chooseUs: ['Seu serviço não é só beleza', 'Cobra mensalidade', 'Quer preço publicado antes de assinar'],
    chooseThem: ['Precisa de volume de clientes novos e aceita dividir a vitrine'],
    faq: [
      {
        question: 'Posso usar os dois ao mesmo tempo?',
        answer:
          'Pode. O marketplace ajuda a ser encontrado, e o perfil público da Controlapag fica para os clientes fixos, com lembrete e mensalidade, nos planos pagos.',
      },
    ],
    publishedAt: '2026-09-28',
    checkedAt: '2026-10-03',
  },
  {
    slug: 'agenda-de-papel',
    other: 'Agenda de papel',
    otherWithArticle: 'a agenda de papel',
    kind: 'manual',
    title: 'Agenda de papel ou agenda online?',
    description:
      'Caderno ou agenda online para marcar horário de clientes: o que cada um resolve, onde o papel falha e quando vale passar para o digital, com o que é pago.',
    h1: 'Agenda de papel ou agenda online?',
    answer:
      'O caderno funciona enquanto só você marca horário e está sempre com ele. Quando o cliente quer marcar fora do seu horário, esquece o compromisso ou atrasa o pagamento, a agenda online deixa o cliente marcar, lembra e cobra.',
    rows: [
      { criterion: 'Quem marca o horário', us: 'Você, ou o cliente pelo link, a qualquer hora', them: 'Só você, com o caderno na mão' },
      { criterion: 'Lembrete ao cliente', us: 'Automático no WhatsApp, 1 por agendamento', them: 'Não tem', paid: true },
      { criterion: 'Remarcação', us: 'O cliente remarca sozinho em horário livre, fora do prazo para desmarcar', them: 'Riscar e reescrever', paid: true },
      { criterion: 'Toda semana no mesmo horário', us: 'Criado uma vez, ocupa a agenda sozinho', them: 'Escrever semana a semana', paid: true },
      { criterion: 'Cobrança', us: `Cobrança avulsa e "Confirmar recebimento"; pelo link, com taxa (${FEES})`, them: 'Anotar quem pagou' },
      { criterion: 'Se perder ou molhar', us: 'Dados guardados na conta, não no aparelho', them: 'Perde tudo' },
      { criterion: 'Custo', us: OUR_PRICE, them: 'Preço do caderno' },
    ],
    chooseUs: [
      'Clientes pedem horário por mensagem fora do expediente',
      'Tem horários fixos semanais',
      'Já perdeu dinheiro com falta ou pagamento esquecido',
    ],
    chooseThem: ['Atende poucas pessoas e sempre no mesmo lugar'],
    faq: [
      {
        question: 'É difícil passar da agenda de papel para a online?',
        answer:
          'Não. Na criação da conta, o assistente monta os serviços e os horários do seu ramo. Depois você cadastra os clientes fixos, um por um, e manda o link para os novos.',
      },
    ],
    publishedAt: '2026-09-28',
    checkedAt: '2026-10-03',
  },
  {
    slug: 'pix-direto-ou-link-de-pagamento',
    other: 'Pix direto na sua chave',
    otherWithArticle: 'o Pix direto',
    usLabel: 'Link de pagamento da Controlapag',
    kind: 'practice',
    title: 'Pix direto ou link de pagamento?',
    description: `Receber por Pix na sua chave ou por link de pagamento: taxa (${PROSE.pixFee}), quando o dinheiro cai, baixa da cobrança e quando cada um faz sentido.`,
    h1: 'Receber por Pix direto ou por link de pagamento?',
    answer: `Pix direto na sua chave não tem taxa da Controlapag e cai na hora, mas você confere o extrato e confirma cada pagamento. O link da Controlapag aceita Pix, cartão e boleto e dá baixa sozinho, com taxa por pagamento: ${PROSE.pixFee}.`,
    rows: [
      { criterion: 'Taxa por pagamento', us: FEES, them: 'Nenhuma da Controlapag; confira a tarifa do seu banco', paid: true },
      { criterion: 'Quanto chega de R$ 100', us: NET_100, them: 'R$ 100,00, menos a tarifa do banco, se houver' },
      {
        criterion: 'Quando o dinheiro cai',
        us: `Pix em ${PAYMENT_FEES.pixSettlementBusinessDays} dias úteis; cartão em ${PAYMENT_FEES.cardSettlementDays} dias`,
        them: 'Na hora, na sua conta',
      },
      {
        criterion: 'Formas de pagamento',
        us: `Pix, cartão (${PAYMENT_FEES.cardBrands.join(' e ')}, à vista) e boleto, se você ligar`,
        them: 'Só Pix',
      },
      { criterion: 'Baixa da cobrança', us: 'Sozinha, quando o cliente paga', them: 'Você confere o extrato e toca em "Confirmar recebimento"' },
      { criterion: 'Plano da Controlapag', us: `Planos pagos, ${PROSE.fromPrice}`, them: 'Qualquer plano, inclusive a conta grátis', paid: true },
    ],
    chooseUsTitle: 'Use o link de pagamento se',
    chooseThemTitle: 'Fique com o Pix direto se',
    chooseUs: [
      'Cliente pede para pagar no cartão',
      'Você perde tempo conferindo extrato e comprovante',
      'Cobra mensalidade e quer a baixa sem fazer nada',
    ],
    chooseThem: ['Tem poucos clientes e confere o extrato todo dia', 'Precisa do dinheiro na conta no mesmo dia'],
    faq: [
      {
        question: 'A Controlapag fica com parte do pagamento?',
        answer: `Não. A taxa por pagamento (${FEES}) é a do processamento do pagamento, e a Controlapag não acrescenta margem.`,
      },
      {
        question: 'Posso receber por Pix direto e usar a Controlapag?',
        answer:
          'Pode, em qualquer plano. O cliente paga na sua chave e você toca em "Confirmar recebimento" e confirma, para a cobrança sair de quem falta pagar.',
      },
    ],
    publishedAt: '2026-10-03',
    checkedAt: '2026-10-03',
  },
  {
    slug: 'cobrar-por-sessao-ou-mensalidade',
    other: 'Cobrança por sessão',
    otherWithArticle: 'a cobrança por sessão',
    usLabel: 'Mensalidade',
    kind: 'practice',
    title: 'Cobrar por sessão ou mensalidade?',
    description:
      'Cobrar cada atendimento ou um valor fixo por mês: a conta com faltas, a previsão de renda, o que acontece na falta e como cada jeito funciona na Controlapag.',
    h1: 'Cobrar por sessão ou mensalidade?',
    answer: `Por sessão, você recebe só o que atendeu: ${SESSIONS_PER_MONTH} sessões de ${formatBRL(SESSION)} viram ${formatBRL(
      SESSION * (SESSIONS_PER_MONTH - 1),
    )} com 1 falta. Na mensalidade de ${formatBRL(MONTHLY)}, você recebe o mesmo valor no mês, e a falta gasta a sessão da cota.`,
    rows: [
      { criterion: 'Como o cliente paga', us: 'Um valor por mês, com uma cota de sessões que não acumula', them: 'Cada atendimento, no dia' },
      {
        criterion: `Exemplo com ${SESSIONS_PER_MONTH} sessões no mês`,
        us: `${formatBRL(MONTHLY)}, com ou sem falta`,
        them: `${formatBRL(SESSION * SESSIONS_PER_MONTH)} sem falta; ${formatBRL(SESSION * (SESSIONS_PER_MONTH - 1))} com 1 falta`,
      },
      { criterion: 'Previsão de renda', us: 'Você sabe no começo do mês quanto vai entrar', them: 'Varia com as faltas e os cancelamentos' },
      { criterion: 'Quando o cliente falta', us: 'A falta gasta a sessão da cota; o cancelamento no prazo devolve', them: 'Horário vazio e sem cobrança, salvo regra combinada' },
      { criterion: 'Cobrança na Controlapag', us: 'Gerada todo mês, com lembrete no WhatsApp', them: 'Cobrança avulsa com vencimento no dia do horário', paid: true },
      { criterion: 'Plano da Controlapag', us: `Planos pagos, ${PROSE.fromPrice}`, them: `Conta grátis, para até ${FREE_PLAN.maxClientsPerMonth} clientes por mês` },
    ],
    chooseUsTitle: 'Cobre mensalidade se',
    chooseThemTitle: 'Cobre por sessão se',
    chooseUs: ['O cliente vem toda semana', 'Falta de última hora já pesou no seu mês', 'Você quer saber quanto vai entrar'],
    chooseThem: ['O cliente vem de vez em quando', 'Cada atendimento tem duração e preço diferentes'],
    faq: [
      {
        question: 'Que desconto dar na mensalidade?',
        answer: `Faça a conta: ${SESSIONS_PER_MONTH} sessões de ${formatBRL(SESSION)} somam ${formatBRL(
          SESSION * SESSIONS_PER_MONTH,
        )}. Uma mensalidade de ${formatBRL(MONTHLY)} dá ${formatBRL(
          SESSION * SESSIONS_PER_MONTH - MONTHLY,
        )} de desconto ao cliente e garante o valor mesmo com falta.`,
      },
      {
        question: 'A sessão que o cliente não usou passa para o mês seguinte?',
        answer:
          'Na Controlapag, não: a cota é do período e não acumula. O cancelamento feito dentro do prazo devolve a sessão; a falta gasta.',
      },
    ],
    publishedAt: '2026-10-03',
    checkedAt: '2026-10-03',
  },
];

export const findComparisonPage = (slug: string): ComparisonPage | undefined =>
  COMPARISON_PAGES.find((page) => page.slug === slug);
